import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { BrandMark } from "@ui";
import { focusRing } from "@ui/tokens/interaction";
import { glassSurface } from "@ui/tokens/glass";
import { useAuth } from "@domain/auth/useAuth";
import { ROUTES } from "@shared/routes";
import { takeReturnTo } from "@screens/auth/returnTo";
import { rememberLogin } from "./lastLogin";
import { googleTokensFromFragment } from "./googleTokens";

export function GoogleCallback() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { handleGoogleCallback } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const complete = async () => {
      const tokens = googleTokensFromFragment(window.location.hash);
      window.history.replaceState(null, "", window.location.pathname);

      try {
        if (!tokens) throw new Error("Aucun jeton de connexion reçu.");

        await handleGoogleCallback(tokens);
        rememberLogin("google");
        navigate(takeReturnTo(), { replace: true });
      } catch (failure) {
        const rejection = failure as { message?: string; detail?: string } | undefined;
        const known = rejection?.message ? t(`auth.errors.${rejection.message}`) : null;
        setError(rejection?.detail ?? (known && !known.startsWith("auth.errors.") ? known : rejection?.message) ?? t("auth.errors.unknownError"));
      }
    };

    void complete();
  }, [navigate, handleGoogleCallback, t]);

  return (
    <div className="app-frame min-h-dvh grid place-items-center px-4">
      <div className="flex flex-col items-center gap-8 w-full max-w-[400px]">
        <BrandMark />
        <div className={`flex flex-col items-center gap-3 w-full rounded-3xl p-8 text-center ${glassSurface}`}>
          {error ? (
            <div role="alert" className="flex flex-col items-center gap-3">
              <span className="grid place-items-center size-12 rounded-full bg-danger/15 text-[color:var(--login-problem)]">
                <ExclamationTriangleIcon className="w-6 h-6" strokeWidth={1.8} aria-hidden="true" />
              </span>
              <h1 className="font-display font-light text-[24px] text-ink-strong">La connexion a échoué</h1>
              <p className="text-body text-ink-body">{error}</p>
              <button
                type="button"
                onClick={() => navigate(ROUTES.login, { replace: true })}
                className={`mt-3 inline-flex items-center justify-center h-12 px-8 rounded-pill bg-primary-fill text-on-primary-fill font-ui text-[15px] font-semibold shadow-primary hover:bg-primary-fill-raised active:translate-y-px ${focusRing}`}
              >
                Retour à la connexion
              </button>
            </div>
          ) : (
            <div role="status" className="flex flex-col items-center gap-3">
              <span
                aria-hidden="true"
                className="size-7 rounded-full border-2 border-[color:var(--login-rule)] border-t-[color:var(--login-step-on)] motion-safe:animate-spin"
              />
              <h1 className="font-display font-light text-[24px] text-ink-strong">Connexion en cours…</h1>
              <p className="text-body text-ink-body">Finalisation avec Google.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
